/**
 * Collection controls that live outside the product grid section (e.g. in the
 * collection hero): grid toggle, filter button and sort dropdown.
 *
 * They drive the main collection section through its public APIs:
 * - `results-list.updateLayout()` switches the grid layout (and remembers it)
 * - `theme-drawer#filters-drawer.open()` opens the filter drawer
 * - `facets-form-component.updateFiltersByURL()` re-sorts without a page reload
 *
 * Blocks: collection-grid-toggle, collection-filter-button, collection-sort.
 */

const desktopQuery = window.matchMedia('(min-width: 750px)');

/**
 * Grid layout values per viewport. "double" is the theme's default layout.
 * "single" is the theme's 1-column mobile layout, and on desktop a 2-column
 * large-card layout (`pacc-large`, styled in pacc-armor.css).
 */
const LAYOUTS = {
  single: { mobile: 'mobile-single', desktop: 'pacc-large' },
  double: { mobile: 'default', desktop: 'default' },
};

class PaccGridToggle extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    this.buttons = [...this.querySelectorAll('button[data-layout]')];
    for (const button of this.buttons) button.addEventListener('click', this.#onClick);
    desktopQuery.addEventListener('change', this.#sync);

    const resultsList = document.querySelector('results-list');
    if (resultsList) {
      // Follow layout changes made elsewhere (restore from a previous visit, viewport resets)
      this.#observer = new MutationObserver(this.#sync);
      this.#observer.observe(resultsList, { subtree: true, attributes: true, attributeFilter: ['product-grid-view'] });
    }
    this.#sync();
  }

  disconnectedCallback() {
    for (const button of this.buttons ?? []) button.removeEventListener('click', this.#onClick);
    desktopQuery.removeEventListener('change', this.#sync);
    this.#observer?.disconnect();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const layout = LAYOUTS[/** @type {keyof typeof LAYOUTS} */ (button.dataset.layout)];
    if (!layout) return;

    const value = desktopQuery.matches ? layout.desktop : layout.mobile;
    const resultsList = /** @type {any} */ (document.querySelector('results-list'));

    if (typeof resultsList?.updateLayout === 'function') {
      // updateLayout reads the value from an input, like the theme's own radio buttons
      const input = document.createElement('input');
      input.value = value;
      resultsList.updateLayout({ target: input });
    } else {
      document.querySelector('.product-grid')?.setAttribute('product-grid-view', value);
    }

    // The grid attribute changes after a view transition; reflect the choice now
    this.#setPressed(button.dataset.layout);
  };

  #sync = () => {
    const view = document.querySelector('results-list .product-grid')?.getAttribute('product-grid-view');
    this.#setPressed(view === LAYOUTS.single.mobile || view === LAYOUTS.single.desktop ? 'single' : 'double');
  };

  /** @param {string | undefined} active */
  #setPressed(active) {
    for (const button of this.buttons) {
      button.setAttribute('aria-pressed', String(button.dataset.layout === active));
    }
  }
}

class PaccFilterButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#onClick);

    // Nothing to open unless the product grid's Filters block has filtering on
    if (!document.getElementById('filters-drawer') && !window.Shopify?.designMode) this.hidden = true;
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = () => {
    const drawer = /** @type {any} */ (document.getElementById('filters-drawer'));
    drawer?.open?.();
  };
}

class PaccSortBy extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sort-toggle]');
    this.panel = this.querySelector('[data-sort-panel]');
    this.options = [...this.querySelectorAll('[data-sort-value]')];
    if (!this.button || !this.panel) return;

    // The markup carries both `popover` and `hidden` (fallback); keep only the one that applies.
    // With popover support, `hidden` would keep the open panel display: none.
    this.supportsPopover = typeof this.panel.showPopover === 'function';
    this.panel.removeAttribute(this.supportsPopover ? 'hidden' : 'popover');

    this.button.addEventListener('click', this.#onToggleClick);
    this.panel.addEventListener('toggle', this.#onPanelToggle);
    for (const option of this.options) option.addEventListener('click', this.#onOptionClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onToggleClick);
    this.panel?.removeEventListener('toggle', this.#onPanelToggle);
    for (const option of this.options ?? []) option.removeEventListener('click', this.#onOptionClick);
    this.#stopTracking();
  }

  get #isOpen() {
    return this.supportsPopover ? this.panel.matches(':popover-open') : !this.panel.hidden;
  }

  #open() {
    if (this.supportsPopover) {
      this.panel.showPopover();
    } else {
      this.panel.hidden = false;
      this.#onPanelToggle();
    }
  }

  #close() {
    if (this.supportsPopover) {
      this.panel.hidePopover();
    } else {
      this.panel.hidden = true;
      this.#onPanelToggle();
    }
  }

  #onToggleClick = () => (this.#isOpen ? this.#close() : this.#open());

  // Popovers close on outside click / Escape by themselves; keep state in sync
  #onPanelToggle = () => {
    const open = this.#isOpen;
    this.button.setAttribute('aria-expanded', String(open));
    if (open) {
      this.#position();
      this.#startTracking();
      /** @type {HTMLElement | undefined} */ (
        this.panel.querySelector('[aria-current="true"]') ?? this.options[0]
      )?.focus({ preventScroll: true });
    } else {
      this.#stopTracking();
    }
  };

  /** Place the panel under the button (popovers render in the top layer, outside the hero) */
  #position = () => {
    if (!this.supportsPopover) return;
    const rect = this.button.getBoundingClientRect();
    const panelWidth = this.panel.offsetWidth;
    const gutter = 8;
    const alignEnd = rect.left + panelWidth > window.innerWidth - gutter;
    const left = alignEnd ? Math.max(gutter, rect.right - panelWidth) : rect.left;

    this.panel.style.top = `${rect.bottom + 8}px`;
    this.panel.style.left = `${left}px`;
  };

  #startTracking() {
    window.addEventListener('scroll', this.#position, { passive: true });
    window.addEventListener('resize', this.#position);
  }

  #stopTracking() {
    window.removeEventListener('scroll', this.#position);
    window.removeEventListener('resize', this.#position);
  }

  /** @param {MouseEvent} event */
  #onOptionClick = (event) => {
    const option = /** @type {HTMLElement} */ (event.currentTarget);
    const value = option.dataset.sortValue;
    if (!value) return;

    for (const item of this.options) item.setAttribute('aria-current', String(item === option));
    this.#close();
    this.button.focus();

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');

    // Same path as the theme's own sort: update the URL and re-render the grid section
    const facetsForm = /** @type {any} */ (document.querySelector('facets-form-component'));
    if (typeof facetsForm?.updateFiltersByURL === 'function') {
      facetsForm.updateFiltersByURL(url.toString());
    } else {
      window.location.assign(url.toString());
    }
  };
}

if (!customElements.get('pacc-grid-toggle')) customElements.define('pacc-grid-toggle', PaccGridToggle);
if (!customElements.get('pacc-filter-button')) customElements.define('pacc-filter-button', PaccFilterButton);
if (!customElements.get('pacc-sort-by')) customElements.define('pacc-sort-by', PaccSortBy);
